// | This module defines a type of native homogeneous Javascript Objects.
// |
// | To maximize performance, Javascript objects are not wrapped,
// | and some native code is used even when it's not necessary.
import * as $runtime from "../runtime.js";
import * as Control$dMonad$dST$dInternal from "../Control.Monad.ST.Internal/index.js";
import * as Data$dArray from "../Data.Array/index.js";
import * as Data$dFoldable from "../Data.Foldable/index.js";
import * as Data$dFunction from "../Data.Function/index.js";
import * as Data$dMaybe from "../Data.Maybe/index.js";
import * as Data$dOrd from "../Data.Ord/index.js";
import * as Data$dShow from "../Data.Show/index.js";
import * as Data$dTuple from "../Data.Tuple/index.js";
import * as Data$dUnfoldable from "../Data.Unfoldable/index.js";
import * as Unsafe$dCoerce from "../Unsafe.Coerce/index.js";
import {_copyST, _fmapObject, _foldM, _foldSCObject, _lookup, _lookupST, _mapWithKey, all, empty, keys, runST, size, toArrayWithKey} from "./foreign.js";
const values = /* #__PURE__ */ toArrayWithKey(v => v1 => v1);
const toUnfoldable = dictUnfoldable => {
  const $0 = toArrayWithKey(Data$dTuple.Tuple);
  return x => Data$dArray.toUnfoldable(dictUnfoldable)($0(x));
};
const toAscUnfoldable = dictUnfoldable => {
  const $0 = Data$dArray.sortWith(Data$dOrd.ordString)(Data$dTuple.fst);
  const $1 = toArrayWithKey(Data$dTuple.Tuple);
  return x => Data$dArray.toUnfoldable(dictUnfoldable)($0($1(x)));
};
const toAscArray = /* #__PURE__ */ toAscUnfoldable(Data$dUnfoldable.unfoldableArray);
const toArray = /* #__PURE__ */ toArrayWithKey(Data$dTuple.Tuple);
const thawST = _copyST;
const singleton = k => v => {
  const $0 = {};
  $0[k] = v;
  return $0;
};
const showObject = dictShow => (
  {show: m => "(fromFoldable " + Data$dShow.showArrayImpl(v => "(Tuple " + Data$dShow.showStringImpl(v._1) + " " + dictShow.show(v._2) + ")")(toArray(m)) + ")"}
);
const mutate = f => m => {
  const s = {...m};
  f(s)();
  return s;
};
const member = $0 => $1 => _lookup(false, v => true, $0, $1);
const mapWithKey = f => m => _mapWithKey(m, f);
const lookup = $0 => $1 => _lookup(Data$dMaybe.Nothing, Data$dMaybe.Just, $0, $1);
const isSubmap = dictEq => m1 => m2 => all(k => v => _lookup(false, dictEq.eq(v), k, m2))(m1);
const isEmpty = /* #__PURE__ */ all(v => v1 => false);
const insert = k => v => mutate($0 => () => {
  $0[k] = v;
  return $0;
});
const functorObject = {map: f => m => _fmapObject(m, f)};
const functorWithIndexObject = {mapWithIndex: mapWithKey, Functor0: () => functorObject};
const fromHomogeneous = () => Unsafe$dCoerce.unsafeCoerce;
const fromFoldableWith = dictFoldable => f => l => {
  const s = {};
  dictFoldable.foldr(x => {
    const $0 = x._1;
    const $1 = _lookupST(x._2, f(x._2), $0, s);
    return b => () => {
      const v$p = $1();
      s[$0] = v$p;
      return b();
    };
  })(() => {})(l)();
  return s;
};
const fromFoldable = dictFoldable => l => {
  const s = {};
  for (const v of Data$dArray.fromFoldableImpl(dictFoldable.foldr)(l)) {
    s[v._1] = v._2;
  }
  return s;
};
const freezeST = _copyST;
const foldMaybe = f => z => m => _foldSCObject(m, z, f, Data$dMaybe.fromMaybe);
const foldM = dictMonad => f => z => _foldM(dictMonad.Bind1().bind)(f)(dictMonad.Applicative0().pure(z));
const union = m => mutate(s => foldM(Control$dMonad$dST$dInternal.monadST)(s$p => k => v => () => {
  s$p[k] = v;
  return s$p;
})(s)(m));
const unions = dictFoldable => dictFoldable.foldl(union)(empty);
const unionWith = f => m1 => m2 => mutate(s1 => foldM(Control$dMonad$dST$dInternal.monadST)(s2 => k => v1 => {
  const $0 = _lookup(v1, v2 => f(v1)(v2), k, m2);
  return () => {
    s2[k] = $0;
    return s2;
  };
})(s1)(m1))(m2);
const semigroupObject = dictSemigroup => ({append: unionWith(dictSemigroup.append)});
const monoidObject = dictSemigroup => ({mempty: empty, Semigroup0: () => ({append: unionWith(dictSemigroup.append)})});
const fold = /* #__PURE__ */ _foldM(Data$dFunction.applyFlipped);
const foldMap = dictMonoid => f => fold(acc => k => v => dictMonoid.Semigroup0().append(acc)(f(k)(v)))(dictMonoid.mempty);
const foldableObject = {foldl: f => fold(z => v => f(z)), foldr: f => z => m => Data$dFoldable.foldrArray(f)(z)(values(m)), foldMap: dictMonoid => f => foldMap(dictMonoid)(v => f)};
const foldableWithIndexObject = {
  foldlWithIndex: f => fold(b => a => f(a)(b)),
  foldrWithIndex: f => z => m => Data$dFoldable.foldrArray(v => f(v._1)(v._2))(z)(toArrayWithKey(Data$dTuple.Tuple)(m)),
  foldMapWithIndex: dictMonoid => foldMap(dictMonoid),
  Foldable0: () => foldableObject
};
const traversableWithIndexObject = {
  traverseWithIndex: dictApplicative => f => ms => fold(acc => k => v => dictApplicative.Apply0().apply(dictApplicative.Apply0().Functor0().map(b => a => mutate($0 => () => {
    $0[k] = a;
    return $0;
  })(b))(acc))(f(k)(v)))(dictApplicative.pure(empty))(ms),
  FunctorWithIndex0: () => functorWithIndexObject,
  FoldableWithIndex1: () => foldableWithIndexObject,
  Traversable2: () => traversableObject
};
const traversableObject = {
  traverse: dictApplicative => x => traversableWithIndexObject.traverseWithIndex(dictApplicative)(v => x),
  sequence: dictApplicative => traversableObject.traverse(dictApplicative)(x => x),
  Functor0: () => functorObject,
  Foldable1: () => foldableObject
};
const filterWithKey = predicate => m => {
  const m$p = {};
  return foldM(Control$dMonad$dST$dInternal.monadST)(acc => k => v => {
    if (predicate(k)(v)) {
      return () => {
        acc[k] = v;
        return acc;
      };
    }
    return () => acc;
  })(m$p)(m)();
};
const filterKeys = predicate => filterWithKey(x => {
  const $0 = predicate(x);
  return v => $0;
});
const filter = predicate => filterWithKey(v => predicate);
const eqObject = dictEq => ({eq: m1 => m2 => isSubmap(dictEq)(m1)(m2) && isSubmap(dictEq)(m2)(m1)});
const ordObject = dictOrd => (
  {compare: m1 => m2 => Data$dOrd.ordArray(Data$dTuple.ordTuple(Data$dOrd.ordString)(dictOrd)).compare(toAscArray(m1))(toAscArray(m2)), Eq0: () => eqObject(dictOrd.Eq0())}
);
const eq1Object = {eq1: dictEq => eqObject(dictEq).eq};
const $$delete = k => mutate($0 => () => {
  delete $0[k];
  return $0;
});
const pop = k => m => {
  const $0 = _lookup(Data$dMaybe.Nothing, Data$dMaybe.Just, k, m);
  if ($0.tag === "Just") {
    return Data$dMaybe.$Maybe(
      "Just",
      Data$dTuple.$Tuple(
        $0._1,
        mutate($1 => () => {
          delete $1[k];
          return $1;
        })(m)
      )
    );
  }
  return Data$dMaybe.Nothing;
};
const alter = f => k => m => {
  const v = f(_lookup(Data$dMaybe.Nothing, Data$dMaybe.Just, k, m));
  if (v.tag === "Nothing") {
    return mutate($0 => () => {
      delete $0[k];
      return $0;
    })(m);
  }
  if (v.tag === "Just") {
    const $0 = v._1;
    return mutate($1 => () => {
      $1[k] = $0;
      return $1;
    })(m);
  }
  $runtime.fail();
};
const update = f => k => m => alter(v2 => {
  if (v2.tag === "Nothing") { return Data$dMaybe.Nothing; }
  if (v2.tag === "Just") { return f(v2._1); }
  $runtime.fail();
})(k)(m);
export {
  alter,
  $$delete as delete,
  eq1Object,
  eqObject,
  filter,
  filterKeys,
  filterWithKey,
  fold,
  foldM,
  foldMap,
  foldMaybe,
  foldableObject,
  foldableWithIndexObject,
  freezeST,
  fromFoldable,
  fromFoldableWith,
  fromHomogeneous,
  functorObject,
  functorWithIndexObject,
  insert,
  isEmpty,
  isSubmap,
  lookup,
  mapWithKey,
  member,
  monoidObject,
  mutate,
  ordObject,
  pop,
  semigroupObject,
  showObject,
  singleton,
  thawST,
  toArray,
  toAscArray,
  toAscUnfoldable,
  toUnfoldable,
  traversableObject,
  traversableWithIndexObject,
  union,
  unionWith,
  unions,
  update,
  values
};
export * from "./foreign.js";
